/**
 * Chat UI store -- Zustand (ephemeral, no persistence).
 *
 * Owns ONLY transient UI state. Server state lives in TanStack Query.
 *
 * State:
 *   - activeChatId: currently open chat
 *   - sidebarOpen: whether sidebar is visible (defaults false on mobile)
 *   - sidebarSearchQuery: current search text in sidebar
 *   - draftMessages: unsent text per chat
 *   - draftAttachments: pending files per chat
 *   - selectedModelId: per-session model override
 *   - selectedAgentId: per-session agent override
 *   - isStreaming / streamingMessageId: streaming state
 *   - scrollToBottomRequested: imperative scroll trigger
 */

import { create } from 'zustand';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ChatStatusFilter = 'active' | 'archived';

export interface BulkActionProgress {
  completed: number;
  total: number;
  failures: string[];
}

interface ChatUIState {
  // Active chat
  activeChatId: string | null;

  // Sidebar
  sidebarOpen: boolean;
  sidebarSearchQuery: string;
  statusFilter: ChatStatusFilter;

  // Classification filters
  filterTopicId: string | null;

  // Drafts (per-chat)
  draftMessages: Record<string, string>;
  draftAttachments: Record<string, File[]>;

  // Model/agent overrides
  selectedModelId: string | null;
  selectedAgentId: string | null;

  // Streaming
  isStreaming: boolean;
  streamingMessageId: string | null;

  // Regeneration
  regeneratingMessageId: string | null;

  // Scroll
  scrollToBottomRequested: boolean;

  // Multi-select / Bulk operations
  isMultiSelectMode: boolean;
  selectedChatIds: Set<string>;
  bulkActionInProgress: boolean;
  bulkActionProgress: BulkActionProgress | null;
}

interface ChatUIActions {
  setActiveChat: (chatId: string | null) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarSearch: (query: string) => void;
  setStatusFilter: (filter: ChatStatusFilter) => void;

  // Classification filters
  setFilterTopicId: (topicId: string | null) => void;
  clearClassificationFilters: () => void;

  setDraftMessage: (chatId: string, text: string) => void;
  setDraftAttachments: (chatId: string, files: File[]) => void;
  clearDraft: (chatId: string) => void;

  setSelectedModel: (modelId: string | null) => void;
  setSelectedAgent: (agentId: string | null) => void;

  setStreaming: (isStreaming: boolean, messageId?: string) => void;

  setRegeneratingMessageId: (messageId: string | null) => void;

  requestScrollToBottom: () => void;
  clearScrollRequest: () => void;

  // Multi-select / Bulk operations
  toggleMultiSelect: () => void;
  setMultiSelectMode: (enabled: boolean) => void;
  toggleChatSelection: (chatId: string) => void;
  selectAllChats: (chatIds: string[]) => void;
  deselectAllChats: () => void;
  setBulkActionInProgress: (inProgress: boolean) => void;
  setBulkProgress: (progress: BulkActionProgress | null) => void;
  resetBulkState: () => void;
}

export type ChatStore = ChatUIState & ChatUIActions;

// ---------------------------------------------------------------------------
// Detect mobile for sidebar default
// ---------------------------------------------------------------------------

function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useChatStore = create<ChatStore>((set) => ({
  // -- state defaults --
  activeChatId: null,
  sidebarOpen: !isMobileViewport(),
  sidebarSearchQuery: '',
  statusFilter: 'active',
  filterTopicId: null,
  draftMessages: {},
  draftAttachments: {},
  selectedModelId: null,
  selectedAgentId: null,
  isStreaming: false,
  streamingMessageId: null,
  regeneratingMessageId: null,
  scrollToBottomRequested: false,
  isMultiSelectMode: false,
  selectedChatIds: new Set<string>(),
  bulkActionInProgress: false,
  bulkActionProgress: null,

  // -- actions --

  setActiveChat: (chatId) =>
    set({ activeChatId: chatId }),

  toggleSidebar: () =>
    set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  setSidebarOpen: (open) =>
    set({ sidebarOpen: open }),

  setSidebarSearch: (query) =>
    set({ sidebarSearchQuery: query }),

  setStatusFilter: (filter) =>
    set({ statusFilter: filter }),

  setFilterTopicId: (topicId) =>
    set({ filterTopicId: topicId }),

  clearClassificationFilters: () =>
    set({ filterTopicId: null }),

  setDraftMessage: (chatId, text) =>
    set((s) => ({
      draftMessages: { ...s.draftMessages, [chatId]: text },
    })),

  setDraftAttachments: (chatId, files) =>
    set((s) => ({
      draftAttachments: { ...s.draftAttachments, [chatId]: files },
    })),

  clearDraft: (chatId) =>
    set((s) => {
      const { [chatId]: _text, ...restMessages } = s.draftMessages;
      const { [chatId]: _files, ...restAttachments } = s.draftAttachments;
      return {
        draftMessages: restMessages,
        draftAttachments: restAttachments,
      };
    }),

  setSelectedModel: (modelId) =>
    set({ selectedModelId: modelId }),

  setSelectedAgent: (agentId) =>
    set({ selectedAgentId: agentId }),

  setStreaming: (isStreaming, messageId) =>
    set({
      isStreaming,
      streamingMessageId: isStreaming ? (messageId ?? null) : null,
    }),

  setRegeneratingMessageId: (messageId) =>
    set({ regeneratingMessageId: messageId }),

  requestScrollToBottom: () =>
    set({ scrollToBottomRequested: true }),

  clearScrollRequest: () =>
    set({ scrollToBottomRequested: false }),

  // -- Multi-select / Bulk operations --

  toggleMultiSelect: () =>
    set((s) => ({
      isMultiSelectMode: !s.isMultiSelectMode,
      selectedChatIds: !s.isMultiSelectMode ? s.selectedChatIds : new Set<string>(),
    })),

  setMultiSelectMode: (enabled) =>
    set({
      isMultiSelectMode: enabled,
      ...(enabled ? {} : { selectedChatIds: new Set<string>() }),
    }),

  toggleChatSelection: (chatId) =>
    set((s) => {
      const next = new Set(s.selectedChatIds);
      if (next.has(chatId)) next.delete(chatId);
      else next.add(chatId);
      return { selectedChatIds: next };
    }),

  selectAllChats: (chatIds) =>
    set({ selectedChatIds: new Set(chatIds) }),

  deselectAllChats: () =>
    set({ selectedChatIds: new Set<string>() }),

  setBulkActionInProgress: (inProgress) =>
    set({ bulkActionInProgress: inProgress }),

  setBulkProgress: (progress) =>
    set({ bulkActionProgress: progress }),

  resetBulkState: () =>
    set({
      isMultiSelectMode: false,
      selectedChatIds: new Set<string>(),
      bulkActionInProgress: false,
      bulkActionProgress: null,
    }),
}));
